import type { CreateMemberResult, CurrentUser, Member, Role } from '@capsio/contracts';
import { useEffect, useState, type ReactNode } from 'react';
import { api, messageOf } from '../api-client.js';
import { BusyButton, Field, PasswordMeter } from '../components/Field.js';

const STATUS_LABEL: Record<Member['status'], { text: string; className: string }> = {
  active: { text: '已激活', className: 'chip ok' },
  pending_first_login: { text: '待首次登录', className: 'chip wait' },
  pending_invite: { text: '待接受邀请', className: 'chip wait' },
};

function CopyField({ value }: { value: string }): ReactNode {
  const [copied, setCopied] = useState(false);
  return (
    <div className="copyfield">
      <span className="val">{value}</span>
      <button
        className="btn quiet"
        type="button"
        onClick={() => {
          void navigator.clipboard?.writeText(value).catch(() => undefined);
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1500);
        }}
      >
        {copied ? '已复制' : '复制'}
      </button>
    </div>
  );
}

/**
 * 成员。系统没有注册入口，所有账号都从这里加。
 *
 * 目前一定是「生成一次性初始密码交给管理员转交」这条路——发信功能的入口撤了，
 * 通道定了之后同一个操作才会改成直接发邀请邮件（`outcome: 'invited'` 那条分支已经写好在等着）。
 */
export function MembersPage({ me }: { me: CurrentUser }): ReactNode {
  const [members, setMembers] = useState<Member[] | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [result, setResult] = useState<CreateMemberResult | null>(null);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('member');
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  /** 正在给谁重置密码；`done` 为 true 表示已经改完，弹窗切成结果那一屏。 */
  const [resetting, setResetting] = useState<{ member: Member; done: boolean } | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetError, setResetError] = useState<string | null>(null);
  const [resetBusy, setResetBusy] = useState(false);

  /** 待确认删除的成员。删人不给二次确认是不行的——这一步删完就找不回来了。 */
  const [deleting, setDeleting] = useState<Member | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  function reload(): void {
    void api.members().then(
      (data) => setMembers(data.members),
      (cause: unknown) => setListError(messageOf(cause)),
    );
  }

  useEffect(reload, []);

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setFormError(null);
    try {
      const created = await api.addMember({ name: name.trim(), email: email.trim(), role });
      setResult(created);
      setName('');
      setEmail('');
      setRole('member');
      reload();
    } catch (cause) {
      setFormError(messageOf(cause));
    } finally {
      setBusy(false);
    }
  }

  function closeAdd(): void {
    setAdding(false);
    setResult(null);
    setFormError(null);
  }

  function openReset(member: Member): void {
    setResetting({ member, done: false });
    setNewPassword('');
    setConfirmPassword('');
    setResetError(null);
  }

  async function submitReset(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    if (resetting === null) return;

    if (newPassword.length < 12) {
      setResetError('密码至少 12 位');
      return;
    }
    if (confirmPassword !== newPassword) {
      setResetError('两次输入不一致');
      return;
    }

    setResetBusy(true);
    setResetError(null);
    try {
      await api.resetMemberPassword(resetting.member.id, newPassword);
      setResetting({ member: resetting.member, done: true });
      reload();
    } catch (cause) {
      setResetError(messageOf(cause));
    } finally {
      setResetBusy(false);
    }
  }

  async function confirmDelete(): Promise<void> {
    if (deleting === null) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await api.deleteMember(deleting.id);
      setDeleting(null);
      reload();
    } catch (cause) {
      setDeleteError(messageOf(cause));
    } finally {
      setDeleteBusy(false);
    }
  }

  return (
    <div className="content-in">
      <div className="page-head">
        <div>
          <h1>成员</h1>
          <p>系统没有注册入口，所有账号都从这里加。</p>
        </div>
        <button className="btn" type="button" onClick={() => setAdding(true)}>
          添加成员
        </button>
      </div>

      {listError !== null && (
        <div className="call crit" role="alert">
          <span className="ic">×</span>
          <p>{listError}</p>
        </div>
      )}

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>姓名</th>
              <th>邮箱</th>
              <th>角色</th>
              <th>状态</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {members === null ? (
              <tr>
                <td colSpan={5} className="dim">
                  加载中…
                </td>
              </tr>
            ) : (
              members.map((member) => (
                <tr key={member.id}>
                  <td className="name">{member.name}</td>
                  <td className="dim">{member.email}</td>
                  <td>
                    <span className="chip plain">
                      {member.role === 'admin' ? '管理员' : '成员'}
                    </span>
                  </td>
                  <td>
                    <span className={STATUS_LABEL[member.status].className}>
                      {STATUS_LABEL[member.status].text}
                    </span>
                  </td>
                  <td className="actions">
                    {/*
                      自己那行不给这两个操作：重置会把自己踢下线再卡在强制改密上，
                      删除则可能让这套部署一个管理员都不剩。想改自己的密码走账号浮层。
                    */}
                    {member.id === me.id ? (
                      <span className="dim-note">本人</span>
                    ) : (
                      <span className="row-links">
                        <button className="linkbtn" type="button" onClick={() => openReset(member)}>
                          重置密码
                        </button>
                        <button
                          className="linkbtn danger"
                          type="button"
                          onClick={() => {
                            setDeleting(member);
                            setDeleteError(null);
                          }}
                        >
                          删除
                        </button>
                      </span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {adding && (
        <div
          className="backdrop"
          onClick={(event) => {
            if (event.target === event.currentTarget) closeAdd();
          }}
        >
          <div className="modal" role="dialog" aria-modal="true" aria-labelledby="add-title">
            {result === null ? (
              <>
                <div className="m-head">
                  <h2 id="add-title">添加成员</h2>
                  <p>会生成一串一次性初始密码，由你线下交给对方。</p>
                </div>
                <form onSubmit={(event) => void submit(event)}>
                  <div className="pair">
                    <Field id="m-name" label="姓名">
                      <input
                        id="m-name"
                        placeholder="李四"
                        value={name}
                        onChange={(event) => setName(event.target.value)}
                      />
                    </Field>
                    <Field id="m-email" label="邮箱" error={formError}>
                      <input
                        id="m-email"
                        type="email"
                        spellCheck={false}
                        placeholder="lisi@example.com"
                        value={email}
                        onChange={(event) => setEmail(event.target.value)}
                      />
                    </Field>
                  </div>
                  <Field id="m-role" label="角色">
                    <select
                      id="m-role"
                      value={role}
                      onChange={(event) => setRole(event.target.value as Role)}
                    >
                      <option value="member">成员</option>
                      <option value="admin">管理员</option>
                    </select>
                  </Field>
                  <div className="row-actions">
                    <button className="btn quiet" type="button" onClick={closeAdd}>
                      取消
                    </button>
                    <BusyButton busy={busy} busyLabel="创建中" type="submit">
                      添加
                    </BusyButton>
                  </div>
                </form>
              </>
            ) : result.outcome === 'invited' ? (
              <>
                <div className="m-head">
                  <h2 id="add-title">邀请邮件已发出</h2>
                  <p>
                    已发往 <strong>{result.member.email}</strong>。对方点开链接自己设密码，链接 7
                    天内有效。
                  </p>
                </div>
                <div className="call ok">
                  <span className="ic">✓</span>
                  <p>成员列表里现在是「待接受邀请」。</p>
                </div>
                <button className="btn" type="button" onClick={closeAdd}>
                  知道了
                </button>
              </>
            ) : (
              <>
                <div className="m-head">
                  <h2 id="add-title">账号已创建</h2>
                  <p>
                    把这串初始密码交给 <strong>{result.member.name}</strong>
                    。它只显示这一次，首次登录后必须改掉。
                  </p>
                </div>
                <CopyField value={result.initialPassword} />
                <div className="call brass">
                  <span className="ic">!</span>
                  <p>系统不会自己发出去。用你们平时的内部渠道转交——微信、内部 IM、当面说都行。</p>
                </div>
                <button className="btn" type="button" onClick={closeAdd}>
                  知道了
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {resetting !== null && (
        <div
          className="backdrop"
          onClick={(event) => {
            if (event.target === event.currentTarget) setResetting(null);
          }}
        >
          <div className="modal" role="dialog" aria-modal="true" aria-labelledby="reset-title">
            {resetting.done ? (
              <>
                <div className="m-head">
                  <h2 id="reset-title">已重置</h2>
                  <p>
                    <strong>{resetting.member.name}</strong>{' '}
                    的密码换成你刚填的那串了。把它交给他，他首次登录后必须自己改掉。
                  </p>
                </div>
                <div className="call brass">
                  <span className="ic">!</span>
                  <p>
                    他在其他设备上的登录已经全部失效。系统不会替你转交，用你们平时的内部渠道说一声。
                  </p>
                </div>
                <button className="btn" type="button" onClick={() => setResetting(null)}>
                  知道了
                </button>
              </>
            ) : (
              <>
                <div className="m-head">
                  <h2 id="reset-title">重置 {resetting.member.name} 的密码</h2>
                  <p>
                    你填一个新密码交给他。这条路不依赖邮件，所以没有发信通道也能用——
                    也正因为如此，密码经过了你的手，他首次登录必须改。
                  </p>
                </div>
                <form onSubmit={(event) => void submitReset(event)}>
                  <Field
                    id="reset-pw"
                    label="新密码"
                    aux="至少 12 位"
                    hint={<PasswordMeter value={newPassword} />}
                  >
                    <input
                      id="reset-pw"
                      type="password"
                      autoComplete="new-password"
                      placeholder="••••••••••••"
                      value={newPassword}
                      onChange={(event) => setNewPassword(event.target.value)}
                    />
                  </Field>
                  <Field id="reset-pw2" label="再输一次" error={resetError}>
                    <input
                      id="reset-pw2"
                      type="password"
                      autoComplete="new-password"
                      placeholder="••••••••••••"
                      value={confirmPassword}
                      onChange={(event) => setConfirmPassword(event.target.value)}
                    />
                  </Field>
                  <div className="row-actions">
                    <button className="btn quiet" type="button" onClick={() => setResetting(null)}>
                      取消
                    </button>
                    <BusyButton busy={resetBusy} busyLabel="重置中" type="submit">
                      重置
                    </BusyButton>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      {deleting !== null && (
        <div
          className="backdrop"
          onClick={(event) => {
            if (event.target === event.currentTarget) setDeleting(null);
          }}
        >
          <div className="modal" role="dialog" aria-modal="true" aria-labelledby="del-title">
            <div className="m-head">
              <h2 id="del-title">删除 {deleting.name}？</h2>
              <p>
                <span className="mono">{deleting.email}</span> 这个账号会被删掉，他会立刻掉线，
                之后用原来的密码也进不来。
              </p>
            </div>

            <div className="call crit">
              <span className="ic">!</span>
              <p>删完找不回来。要让他重新进系统只能再添加一次，那时是一个新账号。</p>
            </div>

            {deleteError !== null && (
              <p className="msg" role="alert">
                {deleteError}
              </p>
            )}

            <div className="row-actions">
              <button className="btn quiet" type="button" onClick={() => setDeleting(null)}>
                取消
              </button>
              <BusyButton
                busy={deleteBusy}
                busyLabel="删除中"
                className="btn danger"
                type="button"
                onClick={() => void confirmDelete()}
              >
                删除
              </BusyButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
